import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Row, Col, Form } from "react-bootstrap";
import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { translateGeneral } from "utils/localizations";
import DocumentsInventoryCreateViewModel from "viewModels/finance/accountingInventory/documentsInventory/documentsInventoryCreateViewModel";
import StepIcon from "./components/StepIcon";

// eslint-disable-next-line sonarjs/cognitive-complexity
const DocumentsInventoryCreateView: ViewComponent<DocumentsInventoryCreateViewModel> = observer(({ vm }) => {
  return (
    <LoaderOverlay loading={vm.busyWatcher.isBusy}>
      <h3 className="m-0 pb-7 text-left">{vm.translate(vm.isEditMode ? "editTitle" : "title")}</h3>
      <div className="table-full" style={{ borderBottom: "1px solid #DDE1E6" }} />
      <Form>
        <Col className="my-6 rounded body-background">
          <Row lg={12} xl={12} className="m-0 py-4">
            <div className="d-flex align-items-center col-md-8 col-sm-4" style={{ gap: "1rem" }}>
              <StepIcon stepNumber={1} isComplete={vm.isPeriodComplete} />
              <span style={{ fontWeight: "500" }}>{vm.translate("periodStep")}</span>
            </div>
            <div className="col-md-2 col-sm-4">
              <AdvancedSelect
                items={vm.fromMonthDate ? [{ id: vm.fromMonthDate, label: vm.fromMonthFormatted }] : []}
                target={vm}
                property="fromMonthDate"
                placeholder={translateGeneral("selectFromList")}
                disabled={true}
              />
            </div>
            <div className="col-md-2 col-sm-4">
              <AdvancedSelect
                items={vm.availableMonths}
                target={vm}
                property="selectedToMonth"
                placeholder={translateGeneral("selectFromList")}
                disabled={vm.createdInventoryId ? !vm.canChangeDate : false}
              />
            </div>
          </Row>

          <div style={{ borderBottom: "1px solid #DDE1E6" }} />
          <Row lg={12} xl={12} className="m-0 py-4">
            <div className="d-flex align-items-center col-md-10 col-sm-8" style={{ gap: "1rem" }}>
              <StepIcon stepNumber={2} isComplete={vm.areAllFilesUploaded} />
              <span style={{ fontWeight: "500" }}>
                {vm.translate("uploadStep")} {vm.uploadedFilesCount}/{vm.totalRequiredFiles}
              </span>
            </div>
            <div className="d-flex justify-content-end col-md-2 col-sm-4">
              <input
                type="file"
                ref={vm.setFileInputRef}
                onChange={e => vm.onFilesSelected(e.target.files)}
                style={{ display: "none" }}
                multiple
                accept=".pdf,.xlsx,.xls"
              />
              <Button
                size="sm"
                iconLeft={IconType.SignPlus}
                style={{ borderRadius: "0.25rem" }}
                variant="primary"
                disabled={!vm.canUpload}
                onClick={vm.onUploadClick}>
                {translateGeneral("uploadFile")}
              </Button>
            </div>
          </Row>
        </Col>

        <Row className="mb-4">
          <Col>
            <table className="table table-full table-striped">
              <thead>
                <tr>
                  <th>{vm.translate("accountLabel")}</th>
                  <th>{vm.translate("accountName")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vm.inventoryAccountsWithFiles.map(accountWithFile => (
                  <tr key={accountWithFile.id}>
                    <td>
                      <span className={accountWithFile.error ? "text-danger fw-bold" : ""}>
                        {accountWithFile.syntheticAccount}-{accountWithFile.analyticAccount}
                      </span>
                    </td>
                    <td>
                      {accountWithFile.fileName ? (
                        <span className={accountWithFile.error ? "text-danger fw-bold" : ""}>{accountWithFile.fileName}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-end">
                      {accountWithFile.hasFile && !accountWithFile.error && (
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            size="sm"
                            iconLeft={IconType.Download}
                            style={{ borderRadius: "0.25rem" }}
                            variant="success"
                            disabled={!accountWithFile.uploadedFile?.inventoryAccountId}
                            onClick={() => {
                              const id = accountWithFile.uploadedFile?.inventoryAccountId;
                              if (id) {
                                vm.onDownloadFile(id);
                              }
                            }}>
                            {translateGeneral("download")}
                          </Button>
                          <Button
                            size="sm"
                            iconLeft={IconType.Trash}
                            style={{ borderRadius: "0.25rem" }}
                            variant="danger"
                            onClick={() => {
                              const id = accountWithFile.uploadedFile?.inventoryAccountId;
                              if (id) {
                                vm.onDeleteFile(id);
                              }
                            }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
        </Row>

        <Row lg={12} xl={12}>
          <div className="col-md-6 col-sm-6">
            <Button size="sm" type="reset" style={{ borderRadius: "0.25rem" }} variant="light" onClick={vm.onClose}>
              {vm.translate("cancel")}
            </Button>
          </div>
          <div className="col-md-6 col-sm-6">
            <div className="d-flex justify-content-end gap-2">
              {vm.isEditMode && vm.canSave && (
                <Button size="sm" style={{ borderRadius: "0.25rem" }} variant="primary" onClick={vm.onSave}>
                  {translateGeneral("save")}
                </Button>
              )}
              {!vm.isEditMode && vm.createdInventoryId && (
                <Button size="sm" style={{ borderRadius: "0.25rem" }} variant="primary" onClick={vm.onClose}>
                  {vm.translate("add")}
                </Button>
              )}
              <Button
                size="sm"
                style={{ borderRadius: "0.25rem" }}
                variant="primary"
                onClick={vm.onStartInventoryCheck}
                disabled={!vm.canStartCheck}>
                {vm.translate("startCheck")}
              </Button>
            </div>
          </div>
        </Row>
      </Form>
    </LoaderOverlay>
  );
});

registerView(DocumentsInventoryCreateView, DocumentsInventoryCreateViewModel);
